import React from 'react';

/*
 * Minimal, dependency-free markdown renderer tuned for chat + notes output.
 * Handles: fenced code blocks (```lang), bold (**x**), inline code (`x`),
 * headings (#–####), tables (| a | b |), bullet lists (-, *) and numbered
 * lists (1.), and paragraphs. Good enough for LLM output; not spec-complete.
 */

let keySeq = 0;
const k = () => `md-${keySeq++}`;

// Inline: **bold** and `code`
function renderInline(text) {
  const nodes = [];
  const re = /(\*\*([^*]+)\*\*|`([^`]+)`)/g;
  let last = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    if (m[2] !== undefined) nodes.push(<strong key={k()}>{m[2]}</strong>);
    else if (m[3] !== undefined) nodes.push(<code key={k()}>{m[3]}</code>);
    last = m.index + m[0].length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

const isTableLine = (l) => /^\s*\|.*\|\s*$/.test(l);
const isBulletLine = (l) => /^\s*[-*]\s+/.test(l);
const isOrderedLine = (l) => /^\s*\d+[.)]\s+/.test(l);
const headingOf = (l) => l.match(/^(#{1,4})\s+(.*)$/);

function renderTable(run) {
  const rows = run.map((l) => l.trim().replace(/^\|/, '').replace(/\|$/, '')
    .split('|').map((c) => c.trim()));
  let header = null;
  let body = rows;
  if (rows.length >= 2 && rows[1].every((c) => /^:?-{2,}:?$/.test(c))) {
    header = rows[0];
    body = rows.slice(2);
  }
  return (
    <div key={k()} className="md-tablewrap">
      <table>
        {header && (
          <thead><tr>{header.map((c) => <th key={k()}>{renderInline(c)}</th>)}</tr></thead>
        )}
        <tbody>
          {body.map((r) => <tr key={k()}>{r.map((c) => <td key={k()}>{renderInline(c)}</td>)}</tr>)}
        </tbody>
      </table>
    </div>
  );
}

function renderTextBlock(block) {
  const lines = block.split('\n').filter((l) => l.trim() !== '');
  if (lines.length === 0) return null;

  const out = [];
  let i = 0;
  while (i < lines.length) {
    const h = headingOf(lines[i]);
    if (h) {
      const Tag = ['h2', 'h2', 'h3', 'h4'][h[1].length - 1];
      out.push(<Tag key={k()}>{renderInline(h[2])}</Tag>);
      i += 1;
    } else if (isTableLine(lines[i])) {
      const run = [];
      while (i < lines.length && isTableLine(lines[i])) run.push(lines[i++]);
      out.push(renderTable(run));
    } else if (isBulletLine(lines[i])) {
      const run = [];
      while (i < lines.length && isBulletLine(lines[i])) run.push(lines[i++]);
      out.push(<ul key={k()}>{run.map((l) => <li key={k()}>{renderInline(l.replace(/^\s*[-*]\s+/, ''))}</li>)}</ul>);
    } else if (isOrderedLine(lines[i])) {
      const run = [];
      while (i < lines.length && isOrderedLine(lines[i])) run.push(lines[i++]);
      out.push(<ol key={k()}>{run.map((l) => <li key={k()}>{renderInline(l.replace(/^\s*\d+[.)]\s+/, ''))}</li>)}</ol>);
    } else {
      const run = [];
      while (i < lines.length && !headingOf(lines[i]) && !isTableLine(lines[i])
             && !isBulletLine(lines[i]) && !isOrderedLine(lines[i])) run.push(lines[i++]);
      out.push(<p key={k()}>{renderInline(run.join(' ').trim())}</p>);
    }
  }
  return out.length === 1 ? out[0] : <React.Fragment key={k()}>{out}</React.Fragment>;
}

export function renderMarkdown(text) {
  if (!text) return null;
  const out = [];
  const fence = /```(\w*)\n?([\s\S]*?)```/g;
  let last = 0;
  let m;
  while ((m = fence.exec(text)) !== null) {
    if (m.index > last) {
      const chunk = text.slice(last, m.index);
      chunk.split(/\n{2,}/).forEach((b) => { const n = renderTextBlock(b); if (n) out.push(n); });
    }
    out.push(
      <pre key={k()}>
        <code>{m[2].replace(/\n$/, '')}</code>
      </pre>
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) {
    text.slice(last).split(/\n{2,}/).forEach((b) => { const n = renderTextBlock(b); if (n) out.push(n); });
  }
  return out;
}

export default renderMarkdown;
